// DashboardScreen.js
import React, { useEffect, useState, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import Topbar from './Topbar';
import Footer from './Footer';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import Slider from 'react-slick';
import Carousel from 'react-bootstrap/Carousel';
import 'bootstrap/dist/css/bootstrap.min.css';
import ImageWithPlaceholder from './ImageLoader';
import CachedImage from './cacheImage'; // import your new component

const languageMapping = {
  en: 'English',
  ms: 'Bahasa Malaysia',
  zh: 'Chinese',
  All: 'All', // Add 'All' if you want it to remain the same
};

const DashboardScreen = () => {
  const [carouselAds, setCarouselAds] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [selectedSorting, setSelectedSorting] = useState("A-Z");
  const [selectedType, setSelectedType] = useState("All");
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const intervalRef = useRef(null);
  const [isVerticalLayout, setIsVerticalLayout] = useState(window.innerWidth < window.innerHeight);

  useEffect(() => {
    fetchCarouselAds();
    const handleResize = () => {
      setIsVerticalLayout(window.innerWidth < window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCarouselAds = async () => {
    try {
      const now = new Date();
      const adsQuery = query(collection(db, 'ads'), where('isActive', '==', true));
      const adsSnapshot = await getDocs(adsQuery);

      const activeAds = adsSnapshot.docs
      .map(doc => ({ ...doc.data(), docId: doc.id }))
      .filter(ad => {
        const from = ad.fromDT.toDate();
        const to   = ad.toDT.toDate();
        return from <= now && to >= now;
      })
      .sort((a, b) =>
        b.toDT.toDate().getTime() - a.toDT.toDate().getTime()
      );
    
      setCarouselAds(activeAds);
      fetchServiceProviders();
    } catch (error) {
      console.error('Error fetching carousel ads:', error);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const providersQuery = query(collection(db, 'serviceProvider'), where('isActive', '==', true),
      where('isShowPublisher', '==', true));
      const providersSnapshot = await getDocs(providersQuery);

      const serviceProvidersData = await Promise.all(
        providersSnapshot.docs.map(async providerDoc => {
          const providerData = { ...providerDoc.data(), docId: providerDoc.id, serviceList: [] };

          try {
            const servicesQuery = query(
              collection(db, 'service'),
              where('serviceProviderId', '==', providerDoc.id),
              where('isActive', '==', true),
              where('isShowItem', '==', true)
            );
            const servicesSnapshot = await getDocs(servicesQuery);

            const services = servicesSnapshot.docs.map(serviceDoc => serviceDoc.data());
            providerData.serviceList = services.sort((a, b) => a.priceStartsFrom - b.priceStartsFrom);
          } catch (e) {
            console.error('Error fetching services for provider:', e);
          }
          return providerData;
        })
      );

      setServiceProviders(serviceProvidersData);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const filteredProviders = serviceProviders
    .filter(provider => {
      if (selectedType !== "All" && !provider.serviceList.some(service => service.category.toLowerCase() === selectedType.toLowerCase())) {
        return false;
      }
      if (selectedLanguage !== "All" && !provider.language.includes(selectedLanguage)) {
        return false;
      }
      return provider.isShowPublisher || true;
    })
    .sort((a, b) => {
      if (selectedSorting === "A-Z") {
        return a.name.localeCompare(b.name);
      } else if (selectedSorting === "Z-A") {
        return b.name.localeCompare(a.name);
      } else if (selectedSorting === "Price ↑") {
        const priceA = a.serviceList.length ? a.serviceList[0].priceStartsFrom : 0;
        const priceB = b.serviceList.length ? b.serviceList[0].priceStartsFrom : 0;
        return priceA - priceB;
      } else if (selectedSorting === "Price ↓") {
        const priceA = a.serviceList.length ? a.serviceList[0].priceStartsFrom : 0;
        const priceB = b.serviceList.length ? b.serviceList[0].priceStartsFrom : 0;
        return priceB - priceA;
      }
      return 0;
    });

  const handleNextAd = () => {
    setCurrentAdIndex((prevIndex) => (prevIndex + 1) % carouselAds.length);
  };

  const handlePrevAd = () => {
    setCurrentAdIndex((prevIndex) => (prevIndex - 1 + carouselAds.length) % carouselAds.length);
  };

/**
 * Given any string, return a fully qualified URL
 * (always https:// if no protocol was given), or null if
 * the input is empty.
 */
  const normalizeHref = (url) => {
    if (!url) return null;

    // If it already has http://, https://, or protocol-relative "//", return it
    if (/^(https?:)?\/\//i.test(url)) {
      // if it’s protocol-relative (e.g. "//foo.com"), add current protocol
      if (url.startsWith("//")) {
        return window.location.protocol + url;
      }
      return url;
    }

    // otherwise assume https
    return `https://${url}`;
  };

  const heightStyle = {
    height: `${window.innerWidth * (9 / 16)}px`,
    width: '100%',
    cursor: 'default',
  };

  return (
    <div style={{ backgroundColor: '#f2f2f2', position: 'relative' }}>
      {/* Fixed Topbar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <Topbar />
      </div>
      {/* Add top margin equal to the Topbar's height (adjust as needed) */}
      <div style={{ marginTop: '56px' }}>
      {carouselAds.length > 0 ? (
        <Carousel
          activeIndex={currentAdIndex}
          onSelect={(selectedIndex) => {
            if (
              currentAdIndex === carouselAds.length - 1 &&
              selectedIndex === 0
            ) {
              handleNextAd();
            } else if (
              currentAdIndex === 0 &&
              selectedIndex === carouselAds.length - 1
            ) {
              handlePrevAd();
            } else if (selectedIndex > currentAdIndex) {
              handleNextAd();
            } else if (selectedIndex < currentAdIndex) {
              handlePrevAd();
            }
          }}
          interval={3000}
          indicators
          controls
          nextIcon={
            <FaArrowRight
              style={{ color: 'white', fontSize: 32, marginRight: 20 }}
            />
          }
          prevIcon={
            <FaArrowLeft
              style={{ color: 'white', fontSize: 32, marginLeft: 20 }}
            />
          }
        >
          {carouselAds.map((ad) => (
            <Carousel.Item key={ad.docId} style={{ overflow: 'hidden' }}>
              <a
                href={normalizeHref(ad.landingPageUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', lineHeight: 0 }}
              >
                <CachedImage
                  src={ad.coverImage}
                  alt={ad.description}
                  style={heightStyle}
                />
              </a>
            </Carousel.Item>
          ))}
        </Carousel>
      ) : (
        <div style={{ width: '100%', overflow: 'hidden' }}>
          <img
            src="/assets/firstimage.jpg" 
            style={heightStyle}
          />
        </div>
      )}


        <div style={{ backgroundColor: '#f2f2f2', paddingBottom: '16px' }}>
          <div
            style={{
              padding: isVerticalLayout ? '20px 32px' : '20px 64px',
              display: isVerticalLayout ? '' : 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: isVerticalLayout ? '' : 'flex',
                gap: isVerticalLayout ? '0px' : '8px',
                flexWrap: isVerticalLayout ? '' : 'wrap',
                height: isVerticalLayout ? '50px' : 'auto',
                overflowY: isVerticalLayout ? 'auto' : 'visible',
              }}
            >
              {['All', 'Articles', 'Videos', 'Others'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  style={{
                    padding: isVerticalLayout ? '8px 12px' : '8px 16px',
                    backgroundColor: selectedType === type ? '#EC3535' : 'white',
                    color: selectedType === type ? 'white' : 'black',
                    border: `1px solid ${selectedType === type ? '#EC3535' : '#D8D8D8'}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: selectedType === type ? 'bold' : 'normal',
                    marginRight: isVerticalLayout ? '4px' : '0px',
                  }}
                >
                  {type}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <div
                style={{
                  padding: isVerticalLayout ? '0px 0px' : '0 8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #D8D8D8',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '14px',
                    color: 'black',
                  }}
                >
                  {['All', 'ms', 'zh', 'en'].map((lang) => (
                    <option key={lang} value={lang}>
                      Language: {languageMapping[lang]}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  padding: '0 8px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #D8D8D8',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <select
                  value={selectedSorting}
                  onChange={(e) => setSelectedSorting(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '14px',
                    color: 'black',
                  }}
                >
                  {['A-Z', 'Z-A', 'Price ↑', 'Price ↓'].map((sort) => (
                    <option key={sort} value={sort}>
                      {sort}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {window.innerWidth <= window.innerHeight * 1.2 && <div style={{ height: '0px' }} />}
        </div>

        <div
          style={{
            display: 'grid',
            gap: '16px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            padding: isVerticalLayout ? '0px 32px' : '0px 64px',
          }}
        >
          {filteredProviders.map(provider => {
            const languageList = provider.language
              .map(lang => ({ en: 'English', zh: 'Chinese', ms: 'Bahasa Malaysia' }[lang] || lang))
              .sort();
            const languageString = languageList.join(', ');

            return (
              <a
                key={provider.docId}
                href={`adwav/provider/${provider.docId}`}
                //target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #D8D8D8',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      backgroundImage: `url(${provider.coverImageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      height: '150px',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <div style={{ padding: '12px' }}>
                    <div style={{ marginBottom: '0px', fontSize: '14px', color: 'black' }}>{provider.category}</div>
                    <div style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold' }}>{provider.name}</div>
                    <div style={{ marginBottom: '0px', fontSize: '14px', color: 'black' }}>Language</div>
                    <div style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold' }}>{languageString}</div>
                    <div style={{ marginBottom: '0px', fontSize: '14px', color: 'black' }}>Monthly Traffic</div>
                    <div style={{ marginBottom: '18px', fontSize: '16px', fontWeight: 'bold' }}>{(provider.monthlyTraffic||0).toLocaleString()}</div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '12px', color: 'black' }}>Price starts from</div>
                        <div style={{ fontSize: '25px', fontWeight: 'bold', color: '#EC3535' }}>
                          {provider.serviceList.length > 0
                            ? `RM ${(provider.serviceList[0].priceStartsFrom||0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                            : 'RM -'}
                        </div>
                      </div>
                      <div
                        href={`/adwav/provider/${provider.docId}`}
                        style={{
                          backgroundColor: '#EC3535',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          textAlign: 'center',
                          marginRight: '8px',
                        }}
                      >
                        View
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div style={{ height: '32px' }}></div>
        
        <Footer />
      </div>
    </div>
  );
};

export default DashboardScreen;
